/**
 * Home "Moments" + Drivers pulse (Sep 2026).
 *
 * One callable (`hubMoments`) feeds the home page:
 *   - Drivers pulse: has the caller nominated anyone this month, and which
 *     VALUES are trending so far. Values only, ranked, never counts and never
 *     people (nominees stay release-gated), and only once a month has
 *     TRENDING_MIN_NOMINATIONS nominations so a lone nomination can't be read.
 *   - Last released month's winners, each with a clap count (`hubClap` toggles
 *     the caller's clap; /driverClaps is server-only).
 *   - "Welcome to DAAily": joiners HR ticked `announceAsJoiner` for, from
 *     their start date on, for JOINER_SHOW_DAYS days. Name, photo, job title
 *     and team only; no date leaves the server (HR data).
 *   - This month's work anniversaries: start month = current month, years
 *     only, no date or day (sorted by name so the order can't leak the day).
 *
 * The date helpers below are shared so the server window and the client
 * countdown agree on the Europe/Zurich calendar.
 */
import { z } from 'zod';
export const MOMENTS_TZ = 'Europe/Zurich';
/** A month's trending values only show once it has at least this many nominations. */
export const TRENDING_MIN_NOMINATIONS = 3;
/** An announced joiner shows for this many days, starting on their start date. */
export const JOINER_SHOW_DAYS = 30;
export const HubClapSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    nomineeEmail: z.string().trim().toLowerCase().email(),
    /** true = clap, false = take the clap back. */
    clap: z.boolean(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Europe/Zurich calendar helpers
// ─────────────────────────────────────────────────────────────────────────────
/** Calendar date in a timezone as 'YYYY-MM-DD'. */
export function zonedDate(now = new Date(), tz = MOMENTS_TZ) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}
/** Days between two 'YYYY-MM-DD' dates (b − a), calendar-exact. */
export function daysBetween(a, b) {
    const toUtc = (s) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
    return Math.round((toUtc(b) - toUtc(a)) / 86_400_000);
}
/** Offset (ms) of a timezone from UTC at a given instant. */
function tzOffsetMs(ts, tz) {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(ts)).map((x) => [x.type, x.value]));
    return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - Math.floor(ts / 1000) * 1000;
}
/** The instant the current month ends (next month's 1st, 00:00) in the timezone. */
export function monthEndInstant(now = new Date(), tz = MOMENTS_TZ) {
    const today = zonedDate(now, tz);
    const y = +today.slice(0, 4);
    const m = +today.slice(5, 7); // 1-12; Date.UTC month index m = next month
    const guess = Date.UTC(y, m, 1);
    let t = guess - tzOffsetMs(guess, tz);
    t = guess - tzOffsetMs(t, tz); // second pass settles a DST change on the boundary
    return new Date(t);
}
/** 'YYYY-MM-DD' plus n calendar days. */
export function addDays(date, n) {
    const t = Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10)) + n * 86_400_000;
    return new Date(t).toISOString().slice(0, 10);
}
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Show in "Welcome to DAAily"? Only when HR ticked the box, and only from the
 * start date on (never before), for JOINER_SHOW_DAYS days.
 */
export function isAnnouncedJoiner(u, today) {
    if (u.announceAsJoiner !== true || !u.employmentStartDate || !ISO_DAY.test(u.employmentStartDate))
        return false;
    const since = daysBetween(u.employmentStartDate, today);
    return since >= 0 && since < JOINER_SHOW_DAYS;
}
/** Years completed this calendar month, or null when the start month isn't this month or it's the first year. */
export function anniversaryYearsThisMonth(start, today) {
    if (!start || !ISO_DAY.test(start) || start.slice(5, 7) !== today.slice(5, 7))
        return null;
    const years = +today.slice(0, 4) - +start.slice(0, 4);
    return years >= 1 ? years : null;
}
/** HR still has to decide on announcing someone who starts tomorrow, and hasn't been reminded yet. */
export function needsJoinerReminder(u, today) {
    return u.announceAsJoiner !== true
        && !u.joinerReminderSentAt
        && u.employmentStartDate === addDays(today, 1);
}
/** Longest countdown shown: the account is usually created weeks, not years, ahead. */
export const STARTER_MAX_DAYS = 365;
/**
 * "Starting soon": active users whose start date is after `today`, for a
 * viewer who is their manager (or sees everyone: HR, admins). The countdown
 * runs from when HR created the account, so the team lead can prepare.
 */
export function upcomingStarters(users, today, viewer) {
    const me = viewer.email.toLowerCase();
    const out = [];
    for (const u of users) {
        if (!u.email || !u.employmentStartDate || !ISO_DAY.test(u.employmentStartDate))
            continue;
        const daysUntil = daysBetween(today, u.employmentStartDate);
        if (daysUntil <= 0 || daysUntil > STARTER_MAX_DAYS)
            continue;
        if (!viewer.seeAll && (u.managerEmail ?? '').toLowerCase() !== me)
            continue;
        if (u.email.toLowerCase() === me)
            continue;
        out.push({
            email: u.email.toLowerCase(), displayName: u.displayName || u.email, photoURL: u.photoURL ?? null,
            jobTitle: u.jobTitle ?? null, team: u.team ?? null, startDate: u.employmentStartDate, daysUntil,
        });
    }
    return out.sort((a, b) => a.daysUntil - b.daysUntil || a.displayName.localeCompare(b.displayName));
}
/**
 * Anniversaries + announced joiners for `today` from the ACTIVE users. Returns
 * no date of any kind; both lists are sorted by name, since a day-based order
 * would leak the date.
 */
export function peopleMoments(users, today) {
    const anniversaries = [];
    const joiners = [];
    for (const u of users) {
        if (!u.email)
            continue;
        const base = { email: u.email.toLowerCase(), displayName: u.displayName || u.email, photoURL: u.photoURL ?? null };
        if (isAnnouncedJoiner(u, today)) {
            joiners.push({ ...base, jobTitle: u.jobTitle ?? null, team: u.team ?? null });
            continue;
        }
        const years = anniversaryYearsThisMonth(u.employmentStartDate, today);
        if (years)
            anniversaries.push({ ...base, years });
    }
    const byName = (a, b) => a.displayName.localeCompare(b.displayName);
    return { anniversaries: anniversaries.sort(byName), joiners: joiners.sort(byName) };
}
//# sourceMappingURL=moments.js.map